import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import BottomNav from "../components/BottomNav";
import {
  Shield, Phone, MapPin, AlertOctagon, Check, X, Trash2, Plus, PhoneCall,
  ArrowLeft, Clock, Users, KeyRound, MessageSquare, EyeOff, Star, Send
} from "lucide-react";

// ============================================================
// Helpers
// ============================================================

const getLoc = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve({ lat: null, lng: null });
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
    () => resolve({ lat: null, lng: null }),
    { timeout: 6000, enableHighAccuracy: true }
  );
});

// Open WhatsApp / SMS with pre-filled text. Falls back gracefully.
function openMessenger(contact, text) {
  const phone = (contact.phone || "").replace(/\s|\+|-|\(|\)/g, "");
  const encoded = encodeURIComponent(text);
  if (phone) {
    // WhatsApp opens in browser or app
    window.open(`https://wa.me/${phone}?text=${encoded}`, "_blank", "noopener");
  } else if (contact.email) {
    window.location.href = `mailto:${contact.email}?subject=${encodeURIComponent("Sentra Safe Date")}&body=${encoded}`;
  }
}

function buildSosMessage({ userName, place, lat, lng, personMeeting, extra = "" }) {
  const map = (lat && lng) ? `https://www.google.com/maps?q=${lat},${lng}` : "";
  return [
    `🚨 SENTRA SAFE DATE`,
    `${userName || "Známá"} tě prosí o pomoc.`,
    place ? `Místo: ${place}` : "",
    personMeeting ? `Osoba: ${personMeeting}` : "",
    map ? `Aktuální poloha: ${map}` : "",
    extra ? extra : "",
    `Prosím zavolej jí nebo napiš.`,
  ].filter(Boolean).join("\n");
}

// ============================================================
// Root
// ============================================================

export default function SafeDate() {
  const nav = useNavigate();
  const [view, setView] = useState("hub");
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [fakeCall, setFakeCall] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingRatingFor, setPendingRatingFor] = useState(null); // session obj

  const load = useCallback(async (opts = {}) => {
    try {
      const [c, a, h] = await Promise.all([
        api.get("/safedate/contacts").catch(() => ({ data: [] })),
        api.get("/safedate/sessions/active").catch(() => ({ data: null })),
        api.get("/safedate/sessions").catch(() => ({ data: [] })),
      ]);
      setContacts(c.data); setActive(a.data); setHistory(h.data);
      if (opts.forceHub) setView("hub");
      else if (a.data) setView("active");
      else setView((v) => (v === "active" ? "hub" : v));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="app-shell bg-aurora min-h-screen pb-nav" data-testid="safedate-page">
      <div className="px-6 pt-10 pb-5 flex items-center gap-3">
        <button onClick={() => nav("/home")} className="text-[#7A8784]" data-testid="sd-back"><ArrowLeft size={20} strokeWidth={1.7} /></button>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#E85D04] font-medium">Sentra Safe</p>
          <h1 className="font-display text-3xl text-[#1A2924]">Safe Date</h1>
        </div>
      </div>

      {loading ? <div className="px-6"><div className="h-40 rounded-3xl shimmer" /></div>
       : view === "active" && active ? (
          <ActiveDate
            session={active}
            onEnd={(session) => { setPendingRatingFor(session); load(); }}
            onFakeCall={() => setFakeCall(true)}
          />
        )
       : view === "new" ? <NewDate contacts={contacts} onCancel={() => setView("hub")} onStarted={() => { setView("active"); load(); }} onAddContact={() => setView("contacts")} />
       : view === "contacts" ? <ContactsView contacts={contacts} onChange={load} onBack={() => setView("hub")} />
       : <Hub contacts={contacts} history={history} onNew={() => setView("new")} onContacts={() => setView("contacts")} />}

      {fakeCall && <FakeCallPro onEnd={() => setFakeCall(false)} />}
      {pendingRatingFor && (
        <DateRatingModal
          session={pendingRatingFor}
          onDone={() => { setPendingRatingFor(null); load({ forceHub: true }); }}
          onSkip={() => { setPendingRatingFor(null); load({ forceHub: true }); }}
        />
      )}
      <BottomNav />
    </div>
  );
}

// ============================================================
// Hub
// ============================================================

function Hub({ contacts, history, onNew, onContacts }) {
  return (
    <div className="px-6 space-y-4">
      <div className="rounded-3xl p-6 bg-gradient-to-br from-[#082A24] to-[#0F3D34] text-white shadow-lg">
        <Shield size={28} className="mb-3" strokeWidth={1.6} />
        <h2 className="font-display text-2xl mb-2">Randi bezpečně</h2>
        <p className="text-sm text-white/85 leading-relaxed mb-5">
          Před schůzkou dej vědět důvěryhodné osobě. Během schůzky check-in, safe word a tichý SOS. Po schůzce hodnocení, ať jsou další holky v bezpečí.
        </p>
        <button onClick={onNew} disabled={contacts.length === 0} className="bg-white text-[#0F3D34] rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-50" data-testid="sd-start-btn">
          {contacts.length === 0 ? "Nejdřív přidej kontakt" : "Začít Safe Date"}
        </button>
      </div>

      <button onClick={onContacts} className="glass-card w-full p-5 flex items-center gap-3" data-testid="sd-contacts-btn">
        <div className="w-11 h-11 rounded-2xl bg-[#FFE4CC] flex items-center justify-center"><Users size={18} className="text-[#0F3D34]" /></div>
        <div className="flex-1 text-left">
          <p className="font-display text-lg text-[#1A2924]">Důvěryhodné kontakty</p>
          <p className="text-xs text-[#7A8784]">{contacts.length} {contacts.length === 1 ? "kontakt" : contacts.length < 5 ? "kontakty" : "kontaktů"}</p>
        </div>
      </button>

      {history.length > 0 && (
        <div className="glass-card p-5">
          <h3 className="font-display text-lg text-[#1A2924] mb-3">Historie schůzek</h3>
          <div className="space-y-2">
            {history.slice(0, 5).map((s) => (
              <div key={s.id} className="border border-[#E5DDD0] rounded-2xl p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#1A2924]">{s.place} {s.person_meeting && `· ${s.person_meeting}`}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === "sos" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {s.status === "sos" ? "🚨 SOS" : "✓ OK"}
                  </span>
                </div>
                <p className="text-xs text-[#7A8784] mt-0.5">{new Date(s.started_at).toLocaleString("cs-CZ")}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Contacts (unchanged UX)
// ============================================================

function ContactsView({ contacts, onChange, onBack }) {
  const [name, setName] = useState(""); const [phone, setPhone] = useState("");
  const [email, setEmail] = useState(""); const [rel, setRel] = useState("kamarádka");
  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/safedate/contacts", { name: name.trim(), phone: phone.trim(), email: email.trim() || null, relation: rel });
      setName(""); setPhone(""); setEmail(""); onChange();
    } catch (e) { alert(e?.response?.data?.detail || "Chyba"); }
  };
  const del = async (id) => { if (!window.confirm("Smazat kontakt?")) return; await api.delete(`/safedate/contacts/${id}`); onChange(); };
  return (
    <div className="px-6 space-y-4">
      <button onClick={onBack} className="text-sm text-[#0F3D34] flex items-center gap-1" data-testid="sd-contacts-back"><ArrowLeft size={16} /> Zpět</button>
      <div className="glass-card p-5 space-y-3" data-testid="add-contact-form">
        <h3 className="font-display text-lg text-[#1A2924]">Přidat kontakt</h3>
        <input className="input-field" placeholder="Jméno *" value={name} onChange={(e) => setName(e.target.value)} data-testid="contact-name" />
        <input className="input-field" placeholder="Telefon (+420...)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="contact-phone" />
        <input className="input-field" type="email" placeholder="E-mail (volitelné)" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="contact-email" />
        <select className="input-field" value={rel} onChange={(e) => setRel(e.target.value)} data-testid="contact-rel">
          <option>kamarádka</option><option>kamarád</option><option>rodič</option><option>sourozenec</option><option>partner/ka</option><option>jiné</option>
        </select>
        <button onClick={add} className="btn-primary" data-testid="add-contact-btn"><Plus size={16} className="inline mr-1" /> Přidat</button>
      </div>
      <div className="space-y-2">
        {contacts.map((c) => (
          <div key={c.id} className="glass-card p-4 flex items-center justify-between" data-testid={`contact-${c.id}`}>
            <div>
              <p className="font-medium text-[#1A2924]">{c.name} <span className="text-xs text-[#7A8784]">· {c.relation}</span></p>
              <p className="text-xs text-[#7A8784]">{c.phone} {c.email && `· ${c.email}`}</p>
            </div>
            <button onClick={() => del(c.id)} className="text-[#7A8784]"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// New Date — with check-in interval + safe word
// ============================================================

function NewDate({ contacts, onCancel, onStarted, onAddContact }) {
  const [selected, setSelected] = useState([]);
  const [place, setPlace] = useState(""); const [who, setWho] = useState("");
  const [time, setTime] = useState(""); const [end, setEnd] = useState("");
  const [shareLoc, setShareLoc] = useState(true);
  const [interval, setInterval] = useState(45);
  const [safeWord, setSafeWord] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const submit = async () => {
    if (selected.length === 0 || !place || !time || !end) { alert("Vyplň všechna povinná pole"); return; }
    setSubmitting(true);
    try {
      await api.post("/safedate/sessions", {
        contact_ids: selected, place, person_meeting: who,
        meeting_time: new Date(time).toISOString(),
        expected_end: new Date(end).toISOString(),
        share_location: shareLoc,
        check_in_interval_min: interval,
        safe_word: safeWord.trim(),
      });
      // Notify contacts up-front (opens WhatsApp with pre-filled announcement)
      const chosenContacts = contacts.filter((c) => selected.includes(c.id));
      if (chosenContacts.length > 0) {
        const msg = `Ahoj! Právě začínám Safe Date na Sentře 💚\nMísto: ${place}${who ? `\nS: ${who}` : ""}\nKonec kolem: ${new Date(end).toLocaleTimeString("cs-CZ",{hour:"2-digit",minute:"2-digit"})}\nBudu se hlásit každých ${interval} min. Pokud se neozvu, prosím zavolej mi.`;
        openMessenger(chosenContacts[0], msg);
      }
      onStarted();
    } catch (e) { alert(e?.response?.data?.detail || "Chyba"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="px-6 space-y-4">
      <button onClick={onCancel} className="text-sm text-[#0F3D34] flex items-center gap-1"><ArrowLeft size={16} /> Zpět</button>
      <h2 className="font-display text-2xl text-[#1A2924]">Nová schůzka</h2>

      <div className="glass-card p-5 space-y-3">
        <div>
          <label className="label-soft">Komu dát vědět *</label>
          {contacts.length === 0 ? (
            <button onClick={onAddContact} className="btn-secondary" data-testid="need-contacts">Nejdřív přidej kontakt</button>
          ) : (
            <div className="flex flex-wrap gap-2">
              {contacts.map((c) => (
                <button key={c.id} onClick={() => toggle(c.id)} data-testid={`pick-${c.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm border ${selected.includes(c.id) ? "bg-[#0F3D34] text-white border-[#0F3D34]" : "bg-white border-[#E5DDD0] text-[#1A2924]"}`}>
                  {selected.includes(c.id) && "✓ "}{c.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div><label className="label-soft">Místo *</label><input className="input-field" value={place} onChange={(e) => setPlace(e.target.value)} placeholder="např. Kavárna Můj Šálek" data-testid="sd-place" /></div>
        <div><label className="label-soft">S kým (volitelné)</label><input className="input-field" value={who} onChange={(e) => setWho(e.target.value)} placeholder="Jméno + tel. nebo Instagram" data-testid="sd-person" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label-soft">Začátek *</label><input type="datetime-local" className="input-field" value={time} onChange={(e) => setTime(e.target.value)} data-testid="sd-start" /></div>
          <div><label className="label-soft">Konec *</label><input type="datetime-local" className="input-field" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="sd-end" /></div>
        </div>

        {/* Check-in interval */}
        <div>
          <label className="label-soft flex items-center gap-1"><Clock size={13} /> Check-in interval</label>
          <div className="flex gap-2" data-testid="checkin-picker">
            {[30, 45, 60, 90].map((n) => (
              <button key={n} type="button" onClick={() => setInterval(n)}
                className={`flex-1 py-2 rounded-full text-sm border ${interval === n ? "bg-[#FF7A00] text-white border-[#FF7A00]" : "bg-white border-[#E5DDD0] text-[#1A2924]"}`}
                data-testid={`interval-${n}`}>
                {n} min
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[#7A8784] mt-1">Každých {interval} min se tě appka zeptá „Jsi v pořádku?" Pokud neodpovíš do 5 min, kontakt dostane alert.</p>
        </div>

        {/* Safe Word */}
        <div>
          <label className="label-soft flex items-center gap-1"><KeyRound size={13} /> Safe Word (volitelné)</label>
          <input className="input-field" value={safeWord} onChange={(e) => setSafeWord(e.target.value)} placeholder="např. červená růže" data-testid="safe-word" maxLength={40} />
          <p className="text-[10px] text-[#7A8784] mt-1">Sdělíš kamarádce toto slovo předem. Když ho pošleš v chatu — appka pozná, že potřebuješ pomoc.</p>
        </div>

        <label className="flex items-center gap-2 text-sm text-[#1A2924]">
          <input type="checkbox" checked={shareLoc} onChange={(e) => setShareLoc(e.target.checked)} data-testid="sd-loc" /> Sdílet polohu s kontakty
        </label>
      </div>

      <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="sd-submit">
        {submitting ? "Startuji..." : "Zahájit Safe Date 🛡️"}
      </button>
    </div>
  );
}

// ============================================================
// Active Date — with countdown, auto check-in, silent SOS, safe word share
// ============================================================

function ActiveDate({ session, onEnd, onFakeCall }) {
  const [showSos, setShowSos] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const [checkinPrompt, setCheckinPrompt] = useState(false);
  const [lastCheckin, setLastCheckin] = useState(session.started_at);
  const [now, setNow] = useState(Date.now());

  const interval = session.check_in_interval_min || 45;
  const expectedEnd = new Date(session.expected_end).getTime();
  const started = new Date(session.started_at).getTime();

  // Live tick
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Auto check-in prompt when interval elapses since last checkin
  useEffect(() => {
    const sinceLast = (now - new Date(lastCheckin).getTime()) / 60000;
    if (sinceLast >= interval && !checkinPrompt && !showEnd && !showSos) {
      setCheckinPrompt(true);
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
    }
  }, [now, lastCheckin, interval, checkinPrompt, showEnd, showSos]);

  const minsElapsed = Math.max(0, Math.round((now - started) / 60000));
  const minsUntilEnd = Math.max(0, Math.round((expectedEnd - now) / 60000));
  const isPastEnd = now > expectedEnd;

  const checkin = async () => {
    const loc = await getLoc();
    await api.post("/safedate/checkin", { session_id: session.id, ok: true, ...loc });
    setLastCheckin(new Date().toISOString());
    setCheckinPrompt(false);
  };

  const triggerSos = async ({ silent = false, trigger = "button" } = {}) => {
    if (!silent && !window.confirm("Odeslat SOS? Otevře se WhatsApp/SMS pro odeslání polohy kontaktům.")) return;
    const loc = await getLoc();
    await api.post("/safedate/sos", { session_id: session.id, silent, trigger, ...loc });
    // Open messenger deep-link for FIRST contact automatically (fastest path)
    const c = session.contacts_snapshot?.[0];
    if (c) {
      const msg = buildSosMessage({
        userName: session.user_name || "",
        place: session.place,
        personMeeting: session.person_meeting,
        lat: loc.lat, lng: loc.lng,
        extra: silent ? "" : "Odeslala jsem SOS z aplikace Sentra Safe Date.",
      });
      openMessenger(c, msg);
    }
    setShowSos(true);
  };

  const sendSafeWord = () => {
    const word = session.safe_word;
    if (!word) { alert("Pro tuto schůzku není nastavené safe word."); return; }
    const c = session.contacts_snapshot?.[0];
    if (!c) return;
    // Send just the safe word — recipient recognizes it as SOS signal
    openMessenger(c, word);
    // Also log silent SOS internally
    triggerSos({ silent: true, trigger: "safe_word" });
  };

  return (
    <div className="px-6 space-y-4">
      <div className={`rounded-3xl p-6 shadow-lg text-white ${session.status === "sos" ? "bg-gradient-to-br from-rose-700 to-rose-500" : "bg-gradient-to-br from-[#082A24] to-[#0F3D34]"}`}>
        <div className="flex items-center gap-2 mb-2">
          <Shield size={16} /><span className="text-xs uppercase tracking-widest">{session.status === "sos" ? "🚨 SOS AKTIVNÍ" : "Aktivní schůzka"}</span>
        </div>
        <p className="font-display text-2xl">{session.place}</p>
        {session.person_meeting && <p className="text-sm text-white/80">s {session.person_meeting}</p>}
        <div className="flex items-center gap-3 mt-3 text-xs text-white/85">
          <span data-testid="mins-elapsed">🕐 {minsElapsed} min</span>
          <span>·</span>
          <span data-testid="until-end">{isPastEnd ? `⏰ ${Math.abs(minsUntilEnd)} min po plánovaném konci` : `Konec za ${minsUntilEnd} min`}</span>
        </div>
        <div className="mt-2 text-[11px] text-white/70">
          {session.contacts_snapshot?.length} kontakt(y) sledují · check-in á {interval} min {session.safe_word && "· 🔑 safe word aktivní"}
        </div>
      </div>

      <button onClick={checkin} className="w-full py-6 rounded-3xl bg-emerald-500 text-white shadow-lg active:scale-95" data-testid="sd-checkin">
        <Check size={28} className="mx-auto mb-1" /><span className="font-display text-xl">Jsem v pořádku ✓</span>
      </button>

      <div className="grid grid-cols-2 gap-3">
        <button onClick={onFakeCall} className="glass-card p-4 flex flex-col items-center gap-1 active:scale-95" data-testid="sd-fake-call">
          <PhoneCall size={22} className="text-[#0F3D34]" /><span className="text-sm font-medium text-[#1A2924]">Falešný hovor</span>
        </button>
        <button onClick={() => setShowEnd(true)} className="glass-card p-4 flex flex-col items-center gap-1 active:scale-95" data-testid="sd-end-btn">
          <X size={22} className="text-[#7A8784]" /><span className="text-sm font-medium text-[#1A2924]">Ukončit</span>
        </button>
      </div>

      {session.safe_word && (
        <button onClick={sendSafeWord} className="w-full py-4 rounded-3xl bg-[#332D38] text-white active:scale-95 flex items-center justify-center gap-2" data-testid="sd-safeword-btn">
          <KeyRound size={18} /> <span className="font-medium">Poslat safe word</span>
          <span className="text-xs text-white/70">„{session.safe_word}"</span>
        </button>
      )}

      <button onClick={() => triggerSos({ silent: true, trigger: "button" })} className="w-full py-4 rounded-3xl bg-[#1A2924] text-white active:scale-95 flex items-center justify-center gap-2" data-testid="sd-silent-sos">
        <EyeOff size={18} /> <span className="font-medium">Tichý SOS</span>
        <span className="text-xs text-white/70">bez alarmu</span>
      </button>

      <button onClick={() => triggerSos({ silent: false, trigger: "button" })} className="w-full py-6 rounded-3xl bg-gradient-to-br from-rose-600 to-rose-500 text-white shadow-2xl active:scale-95" data-testid="sd-sos">
        <AlertOctagon size={32} className="mx-auto mb-1" /><span className="font-display text-2xl">SOS</span>
        <p className="text-xs text-white/85 mt-1">Otevře WhatsApp s tvou polohou pro kontakt</p>
      </button>

      {/* Auto check-in prompt */}
      {checkinPrompt && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" data-testid="checkin-prompt">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full text-center">
            <div className="text-5xl mb-3">👋</div>
            <h3 className="font-display text-2xl text-[#1A2924] mb-2">Jsi v pořádku?</h3>
            <p className="text-sm text-[#54635F] mb-5">Uplynulo {interval} min od posledního check-inu.</p>
            <div className="flex gap-3">
              <button onClick={() => triggerSos({ silent: false, trigger: "no_response" })} className="flex-1 py-3 rounded-full bg-rose-600 text-white font-medium" data-testid="prompt-help">Potřebuji pomoc</button>
              <button onClick={checkin} className="flex-1 py-3 rounded-full bg-emerald-500 text-white font-medium" data-testid="prompt-ok">V pohodě ✓</button>
            </div>
          </div>
        </div>
      )}

      {showSos && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" data-testid="sos-sent-modal">
          <div className="bg-white rounded-3xl p-6 max-w-sm text-center">
            <AlertOctagon size={40} className="mx-auto text-rose-600 mb-3" />
            <h3 className="font-display text-xl text-[#1A2924] mb-2">SOS odesláno</h3>
            <p className="text-sm text-[#54635F] mb-4">Otevřeli jsme WhatsApp s tvou polohou. Odešli zprávu. Kontakt dostane vědět, kde jsi a co potřebuješ.</p>
            <button onClick={() => setShowSos(false)} className="btn-primary">Rozumím</button>
          </div>
        </div>
      )}

      {showEnd && <EndDialog session={session} onCancel={() => setShowEnd(false)} onDone={() => { setShowEnd(false); onEnd(session); }} />}
    </div>
  );
}

// ============================================================
// End dialog (just confirms end — rating done in modal after)
// ============================================================

function EndDialog({ session, onCancel, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/safedate/end", { session_id: session.id });
      onDone();
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end p-0" data-testid="end-modal">
      <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl p-6">
        <div className="w-12 h-1 bg-[#E5DDD0] rounded-full mx-auto mb-4" />
        <h3 className="font-display text-2xl text-[#1A2924] mb-1">Jsi doma? 🏠</h3>
        <p className="text-sm text-[#54635F] mb-4">Potvrď návrat a ohodnoť schůzku — pomůžeš dalším holkám na Sentře.</p>
        <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="end-confirm">
          {submitting ? "Ukončuji..." : "Ano, jsem doma bezpečně ✓"}
        </button>
        <button onClick={onCancel} className="block w-full text-center text-sm text-[#7A8784] mt-3">Ještě ne</button>
      </div>
    </div>
  );
}

// ============================================================
// Date Rating Modal (5 questions → Safety Score)
// ============================================================

function DateRatingModal({ session, onDone, onSkip }) {
  const [feltSafe, setFeltSafe] = useState(0);
  const [profileMatch, setProfileMatch] = useState(null);
  const [onTime, setOnTime] = useState(null);
  const [respectful, setRespectful] = useState(0);
  const [crossed, setCrossed] = useState(null);
  const [wouldMeetAgain, setWouldMeetAgain] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = feltSafe > 0 && profileMatch !== null && onTime !== null && respectful > 0 && crossed !== null;

  const submit = async () => {
    if (!canSubmit) { setError("Odpověz prosím na všechny hvězdičky a otázky."); return; }
    setSubmitting(true); setError("");
    try {
      await api.post("/safedate/rate", {
        session_id: session.id,
        rated_person_name: session.person_meeting || "",
        felt_safe: feltSafe,
        profile_matches_reality: !!profileMatch,
        was_on_time: !!onTime,
        was_respectful: respectful,
        crossed_boundaries: !!crossed,
        would_meet_again: wouldMeetAgain,
        comment,
      });
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || "Chyba při ukládání.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto" data-testid="date-rating-modal">
      <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl sm:rounded-3xl p-6 my-0 sm:my-4">
        <div className="w-12 h-1 bg-[#E5DDD0] rounded-full mx-auto mb-4 sm:hidden" />
        <div className="text-center mb-4">
          <div className="text-4xl mb-1">💛</div>
          <h3 className="font-display text-2xl text-[#1A2924]">Jak to bylo?</h3>
          <p className="text-xs text-[#7A8784] mt-1">Anonymně. Pomůžeš dalším holkám poznat bezpečné lidi.</p>
        </div>

        <div className="space-y-4">
          <RatingRow label="Cítila ses bezpečně?" value={feltSafe} onChange={setFeltSafe} testid="rate-safe" />
          <YesNoRow label="Odpovídal profil realitě?" value={profileMatch} onChange={setProfileMatch} testid="rate-profile" />
          <YesNoRow label="Přišel včas?" value={onTime} onChange={setOnTime} testid="rate-ontime" />
          <RatingRow label="Byl respektující?" value={respectful} onChange={setRespectful} testid="rate-respect" />
          <YesNoRow label="Šel přes tvé hranice?" value={crossed} onChange={setCrossed} testid="rate-crossed" warning />
          <YesNoRow label="Šla bys znovu?" value={wouldMeetAgain} onChange={setWouldMeetAgain} testid="rate-again" optional />
          <div>
            <label className="label-soft">Komentář (volitelné)</label>
            <textarea rows={2} className="input-field text-sm" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Něco, co by se ostatní holky měly dozvědět..." maxLength={500} data-testid="rate-comment" />
          </div>
        </div>

        {error && <p className="text-xs text-rose-600 mt-3">{error}</p>}

        <div className="flex gap-3 mt-5">
          <button onClick={onSkip} className="flex-1 py-3 rounded-full border border-[#E5DDD0] text-[#7A8784]" data-testid="rate-skip">Přeskočit</button>
          <button onClick={submit} disabled={!canSubmit || submitting} className="flex-1 py-3 rounded-full bg-[#0F3D34] text-white font-medium disabled:opacity-40" data-testid="rate-submit">
            {submitting ? "Ukládám..." : "Odeslat"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RatingRow({ label, value, onChange, testid }) {
  return (
    <div>
      <p className="text-sm text-[#1A2924] mb-1.5">{label}</p>
      <div className="flex gap-2" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => onChange(n)} className={`flex-1 py-2 rounded-2xl border transition ${value >= n ? "bg-[#FF7A00] border-[#FF7A00]" : "bg-white border-[#E5DDD0]"}`} data-testid={`${testid}-${n}`}>
            <Star size={18} className={`mx-auto ${value >= n ? "text-white fill-white" : "text-[#B8B4AD]"}`} strokeWidth={1.5} />
          </button>
        ))}
      </div>
    </div>
  );
}

function YesNoRow({ label, value, onChange, testid, warning, optional }) {
  return (
    <div>
      <p className="text-sm text-[#1A2924] mb-1.5">{label} {optional && <span className="text-[10px] text-[#7A8784]">(volitelné)</span>}</p>
      <div className="grid grid-cols-2 gap-2" data-testid={testid}>
        <button onClick={() => onChange(true)} className={`py-2.5 rounded-2xl text-sm border font-medium ${value === true ? (warning ? "bg-rose-600 text-white border-rose-600" : "bg-[#0F3D34] text-white border-[#0F3D34]") : "bg-white border-[#E5DDD0] text-[#1A2924]"}`} data-testid={`${testid}-yes`}>Ano</button>
        <button onClick={() => onChange(false)} className={`py-2.5 rounded-2xl text-sm border font-medium ${value === false ? "bg-emerald-500 text-white border-emerald-500" : "bg-white border-[#E5DDD0] text-[#1A2924]"}`} data-testid={`${testid}-no`}>Ne</button>
      </div>
    </div>
  );
}

// ============================================================
// Fake Call PRO — caller picker + delay picker
// ============================================================

const CALLERS = [
  { key: "mom", name: "Mama", sub: "mobil" },
  { key: "boss", name: "Šéf", sub: "kancelář" },
  { key: "friend", name: "Klára ❤️", sub: "kamarádka" },
  { key: "sister", name: "Ségra", sub: "mobil" },
];

function FakeCallPro({ onEnd }) {
  const [stage, setStage] = useState("setup"); // setup | ringing | answered
  const [caller, setCaller] = useState(CALLERS[0]);
  const [customName, setCustomName] = useState("");
  const [delay, setDelay] = useState(0); // seconds
  const [answered, setAnswered] = useState(false);
  const timerRef = useRef(null);

  const displayName = customName.trim() || caller.name;

  const scheduleRing = (sec) => {
    setStage("ringing"); // switch UI even if delayed — we show "za X s"
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate([500, 300, 500, 300, 500]);
      setDelay(0);
    }, sec * 1000);
    setDelay(sec);
    // If sec === 0, still start vibration immediately
    if (sec === 0 && navigator.vibrate) navigator.vibrate([500, 300, 500, 300, 500]);
  };

  useEffect(() => {
    return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
  }, []);

  // Vibration loop while ringing (not answered, delay elapsed)
  useEffect(() => {
    if (stage !== "ringing" || answered || delay > 0) return;
    const iv = window.setInterval(() => { if (navigator.vibrate) navigator.vibrate([500, 300, 500]); }, 3000);
    return () => window.clearInterval(iv);
  }, [stage, answered, delay]);

  // Countdown display
  const [countdown, setCountdown] = useState(0);
  useEffect(() => {
    if (delay <= 0) { setCountdown(0); return; }
    setCountdown(delay);
    const iv = window.setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => window.clearInterval(iv);
  }, [delay]);

  // === Setup screen ===
  if (stage === "setup") {
    return (
      <div className="fixed inset-0 z-[100] bg-gradient-to-b from-[#1a2924] to-[#082A24] text-white p-6 overflow-y-auto" data-testid="fake-call-setup">
        <button onClick={onEnd} className="text-white/70 flex items-center gap-1 text-sm mb-6"><ArrowLeft size={16} /> Zavřít</button>
        <h2 className="font-display text-3xl mb-2">Falešný hovor</h2>
        <p className="text-sm text-white/70 mb-6">Vyber, kdo „zavolá" a za jak dlouho. Použij, když potřebuješ důvod odejít.</p>

        <div className="mb-5">
          <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Volá:</label>
          <div className="grid grid-cols-2 gap-2">
            {CALLERS.map((c) => (
              <button key={c.key} onClick={() => { setCaller(c); setCustomName(""); }}
                className={`p-3 rounded-2xl text-left border ${caller.key === c.key && !customName ? "bg-[#FF7A00] border-[#FF7A00]" : "bg-white/5 border-white/10"}`}
                data-testid={`caller-${c.key}`}>
                <p className="font-medium">{c.name}</p>
                <p className="text-xs text-white/60">{c.sub}</p>
              </button>
            ))}
          </div>
          <input value={customName} onChange={(e) => setCustomName(e.target.value)}
            placeholder="Nebo vlastní jméno..."
            className="w-full mt-3 px-4 py-3 rounded-2xl bg-white/10 border border-white/10 text-white placeholder-white/40 text-sm"
            data-testid="caller-custom" />
        </div>

        <div className="mb-8">
          <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Za jak dlouho zazvoní:</label>
          <div className="grid grid-cols-4 gap-2" data-testid="delay-picker">
            {[
              { s: 0, label: "Hned" },
              { s: 30, label: "30 s" },
              { s: 60, label: "1 min" },
              { s: 300, label: "5 min" },
            ].map((d) => (
              <button key={d.s} onClick={() => scheduleRing(d.s)}
                className="py-3 rounded-2xl bg-white/10 border border-white/10 text-sm active:scale-95"
                data-testid={`delay-${d.s}`}>
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-white/50 text-center">Tip: vypni budík telefonu na tichý — vibrace zůstane. Nikdo ve tvém okolí neuvidí, co jsi klikla.</p>
      </div>
    );
  }

  // === Ringing screen ===
  return (
    <div className="fixed inset-0 z-[100] bg-gradient-to-b from-[#1a2924] to-[#082A24] flex flex-col items-center justify-between p-8 text-white" data-testid="fake-call">
      <div className="mt-16 text-center">
        {countdown > 0 ? (
          <>
            <p className="text-white/60 text-sm uppercase tracking-widest">Zvoní za</p>
            <p className="font-display text-6xl mt-2" data-testid="countdown">{countdown}</p>
            <p className="text-white/50 text-xs mt-3">Dělej, že telefonuješ, dokud nezazvoní.</p>
          </>
        ) : (
          <>
            <p className="text-white/60 text-sm uppercase tracking-widest">Příchozí hovor</p>
            <p className="font-display text-4xl mt-2">{displayName}</p>
            <p className="text-white/70 mt-1">{answered ? "Volání..." : caller.sub}</p>
          </>
        )}
      </div>
      <div className="w-32 h-32 rounded-full bg-white/10 flex items-center justify-center heart-pulse">
        <Phone size={48} className="text-white" />
      </div>
      <div className="flex gap-8 mb-8">
        {countdown > 0 ? (
          <button onClick={onEnd} className="text-white/70 text-sm underline">Zrušit</button>
        ) : !answered ? (
          <>
            <button onClick={onEnd} className="w-16 h-16 rounded-full bg-rose-600 flex items-center justify-center shadow-lg active:scale-90" data-testid="fake-decline">
              <Phone size={24} className="text-white rotate-[135deg]" />
            </button>
            <button onClick={() => setAnswered(true)} className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg active:scale-90" data-testid="fake-answer">
              <Phone size={24} className="text-white" />
            </button>
          </>
        ) : (
          <button onClick={onEnd} className="w-20 h-20 rounded-full bg-rose-600 flex items-center justify-center shadow-lg" data-testid="fake-hangup">
            <Phone size={28} className="text-white rotate-[135deg]" />
          </button>
        )}
      </div>
    </div>
  );
}
